/**
 * Field match, this side of the service binding (Sep 12 2026).
 *
 * The classifying half does not live here and must not. Reading a student's
 * completion note is allowed in exactly one place, `p3-milestones-reader`,
 * which holds the Admin credential and whose containment comment says so; it
 * sends `firstRole { role, industry }` and never a word of the note. What is
 * left for this side is the comparison, so this file is the twin of the
 * reader's `canonicalIndustry` and `fieldMatch` and nothing else. There is no
 * lexicon here, and there should never be one: if a label needs deciding, the
 * decision belongs in the Worker that is allowed to see the text.
 *
 * Shared by the Worker's Milestones section and the sample, like
 * `outcome-milestones.ts`, so the two cannot disagree about what counts.
 */

/** The platform's industries, as the Opportunities form and student profiles name them. */
export const INDUSTRIES = [
  "Agriculture",
  "Arts & Entertainment",
  "Athletics",
  "Business",
  "Commerce & Retail",
  "Communications",
  "Community & Social Services",
  "Education or Academia",
  "Government",
  "Health & Medicine",
  "Law",
  "Other",
  "Science and Research",
  "Technology",
] as const;

export type Industry = (typeof INDUSTRIES)[number];

/**
 * A first role as the reader classified it. Both halves are labels or null;
 * neither is ever text a student wrote.
 *
 * `role` is the occupation's industry ("registered nurse" is Health & Medicine
 * wherever they work). `industry` is the sector the note stated ("in
 * healthcare"). The figure uses the occupation first: counting a match on
 * either would count a nurse who mentions a software tool as Technology.
 */
export interface FirstRole {
  readonly role: Industry | null;
  readonly industry: Industry | null;
}

/** Whitespace and case folded, so "health & medicine" matches the label. */
const key = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, " ");

const CANONICAL = new Map<string, Industry>(INDUSTRIES.map((label) => [key(label), label]));

/**
 * One of the fourteen, or null. Takes a label or the `{ name }` object the
 * student index sometimes returns for `industry`: the index is an external
 * input and has already shipped an object where a string was promised
 * (`meetFrequency`, Sep 10 2026), so this never trusts the declaration.
 */
export function canonicalIndustry(value: unknown): Industry | null {
  const name =
    typeof value === "string"
      ? value
      : value !== null && typeof value === "object"
        ? (value as { readonly name?: unknown }).name
        : undefined;
  if (typeof name !== "string") return null;
  return CANONICAL.get(key(name)) ?? null;
}

/** The one label a figure uses: the occupation's, else the sector stated. */
export function firstRoleIndustry(first: FirstRole | undefined): Industry | null {
  return first ? (first.role ?? first.industry) : null;
}

/**
 * Whether a student's first role sits in their own field.
 *
 * Null, not false, whenever the question cannot be asked: no classified role,
 * no profile industry, or "Other" on either side. "Other" is a bucket for
 * everything outside the thirteen named fields, so two students in "Other"
 * have not been shown to share a field and must not be counted as a match.
 * Null keeps them out of the denominator as well as the numerator, which is
 * why the figure is a rate over the comparable students and not over the
 * cohort.
 */
export function fieldMatch(first: FirstRole | undefined, profileIndustry: unknown): boolean | null {
  const label = firstRoleIndustry(first);
  const profile = canonicalIndustry(profileIndustry);
  if (label === null || label === "Other") return null;
  if (profile === null || profile === "Other") return null;
  return label === profile;
}

/**
 * The cohort's field match: how many of the students who can be compared are
 * working in their own field, and how many could be compared at all.
 *
 * The denominator is deliberately NOT the cohort. A school with 400 students
 * and three first roles on record would otherwise publish "0.75% field match",
 * which says nothing true about the school and everything about how few
 * students have reached that milestone. Both parts still go through the floor
 * in `rate()`, which is what keeps three students from being published at all.
 */
export function fieldMatchCounts(
  pairs: Iterable<{ readonly first: FirstRole | undefined; readonly profileIndustry: unknown }>,
): { readonly matched: number; readonly comparable: number } {
  let matched = 0;
  let comparable = 0;
  for (const pair of pairs) {
    const verdict = fieldMatch(pair.first, pair.profileIndustry);
    if (verdict === null) continue;
    comparable++;
    if (verdict) matched++;
  }
  return { matched, comparable };
}
